import type { DeliveryMethodConfig, FreeShippingRule } from "@/types";

/**
 * Configuração central da loja.
 *
 * Todos os dados comerciais, de entrega e de frete grátis ficam concentrados
 * aqui. A ideia é que o futuro painel administrativo consiga editar estes
 * valores sem precisar mexer nos componentes.
 *
 * IMPORTANTE: os valores de frete, prazos e regras são demonstrativos.
 */

export const storeConfig = {
  name: "Auréa",
  signature: "Passos que acompanham cada versão de você.",
  origin: "Site Auréa — modelo demonstrativo",
  whatsappNumber: "5562998023960",
  whatsappDisplay: "atendimento pelo WhatsApp",
  instagramUrl: "https://www.instagram.com/vellora__studio/",
  velloraUrl: "https://vellorastudio.suapagina.net.br/",
  contactNote: "Atendimento digital em todo o Brasil",
};

/** Configuração comercial (desconto). */
export const commercialConfig = {
  discount: {
    enabled: true,
    threshold: 600,
    rate: 0.05,
    label: "Desconto demonstrativo de 5% em compras a partir de R$ 600,00.",
  },
};

/** Formas de recebimento demonstrativas. */
export const deliveryMethods: DeliveryMethodConfig[] = [
  {
    id: "retirada",
    name: "Retirada",
    description: "Você retira o produto combinando o local e o horário pelo atendimento.",
    deadline: "O local e o horário serão confirmados no atendimento.",
    fee: 0,
    regions: "Goiânia — GO e região (confirmar no atendimento).",
    notice: "Sem taxa de entrega.",
    available: true,
  },
  {
    id: "entrega-local",
    name: "Entrega local",
    description: "Levamos até você na região combinada, com cuidado e agilidade.",
    deadline: "Até 2 dias úteis (demonstrativo).",
    fee: 15,
    regions: "Goiânia — GO. Fora da região: consultar no atendimento.",
    notice: "Taxa demonstrativa, sujeita à confirmação no atendimento.",
    available: true,
    requiresAddress: true,
  },
  {
    id: "pac",
    name: "Correios — PAC",
    description: "Envio econômico pelos Correios, ideal quando o prazo não é urgente.",
    deadline: "De 5 a 10 dias úteis (demonstrativo).",
    fee: 24.9,
    regions: "Todo o Brasil, conforme cobertura dos Correios.",
    notice: "O valor definitivo será confirmado no atendimento.",
    available: true,
    requiresCep: true,
    requiresAddress: true,
  },
  {
    id: "sedex",
    name: "Correios — SEDEX",
    description: "Envio mais rápido pelos Correios para quem tem pressa.",
    deadline: "De 2 a 5 dias úteis (demonstrativo).",
    fee: 39.9,
    regions: "Todo o Brasil, conforme cobertura dos Correios.",
    notice: "O valor definitivo será confirmado no atendimento.",
    available: true,
    requiresCep: true,
    requiresAddress: true,
  },
  {
    id: "transportadora",
    name: "Transportadora",
    description: "Envio por transportadora parceira, com mais segurança no transporte.",
    deadline: "De 4 a 8 dias úteis (demonstrativo).",
    fee: 35,
    regions: "Capitais e cidades atendidas pelas transportadoras parceiras.",
    notice: "A disponibilidade será confirmada no atendimento.",
    available: true,
    requiresCep: true,
    requiresAddress: true,
  },
  {
    id: "onibus",
    name: "Envio por ônibus",
    description: "Enviamos para cidades atendidas por linhas parceiras, com retirada na rodoviária.",
    deadline: "Horário e disponibilidade a confirmar no atendimento.",
    fee: 25,
    regions: "Cidades atendidas por linhas parceiras.",
    notice: "A disponibilidade, o horário e o ponto de retirada serão confirmados no atendimento.",
    available: true,
    requiresBus: true,
  },
  {
    id: "consultar",
    name: "Consultar no atendimento",
    description: "Prefere combinar de outro jeito? A gente resolve tudo com você pelo WhatsApp.",
    deadline: "A combinar no atendimento.",
    fee: null,
    regions: "Todo o Brasil.",
    notice: "Taxa a confirmar no atendimento.",
    available: true,
  },
];

/** Regras de frete grátis (demonstrativas e configuráveis). */
export const freeShippingRules: FreeShippingRule[] = [
  {
    id: "goiania-entrega-local-500",
    label: "Entrega local gratuita em Goiânia para pedidos a partir de R$ 500,00",
    enabled: true,
    methodIds: ["entrega-local"],
    minValue: 500,
    cities: ["Goiânia"],
  },
];

export const deliveryMethodById = (id: string): DeliveryMethodConfig | undefined =>
  deliveryMethods.find((method) => method.id === id);